import { useEffect, useRef } from 'react';
import { backgroundPaused } from './api.js';

/**
 * ehr#86 - keep a screen current without a page refresh. Calls `refresh` every `everyMs`
 * while the tab is visible, and straight away when the user comes back to the tab
 * (focus / visible again), at most once per `minGapMs`. Nothing runs while the tab is
 * hidden. The EHR has no push channel from the Calendar or the portal, so this is how
 * changes made there (an appointment confirmed, a status changed) reach the Dashboard.
 */
export function useLiveRefresh(refresh: () => void, everyMs = 20_000, minGapMs = 3_000): void {
  const fn = useRef(refresh);
  fn.current = refresh;
  useEffect(() => {
    let last = Date.now();
    const run = () => {
      if (document.visibilityState !== 'visible') return;
      if (backgroundPaused()) return; // ehr#859: the server asked us to slow down
      if (Date.now() - last < minGapMs) return;
      last = Date.now();
      fn.current();
    };
    const timer = window.setInterval(run, everyMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', run);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', run);
    };
  }, [everyMs, minGapMs]);
}

/**
 * ehr#86 - near-instant updates: ask a tiny "version" endpoint every `everyMs` while the
 * tab is visible (and at once on coming back), and call `onChange` only when the version
 * it returns differs from the last one seen. The full reload happens only on a real
 * change, so checking every few seconds stays cheap.
 */
export function useLiveVersion(
  load: () => Promise<{ version: string }>,
  onChange: () => void,
  everyMs = 3_000,
): void {
  const loadRef = useRef(load);
  loadRef.current = load;
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  useEffect(() => {
    let seen: string | null = null;
    let busy = false;
    let live = true;
    const check = () => {
      // ehr#859: nor while the server has asked us to slow down
      if (busy || document.visibilityState !== 'visible' || backgroundPaused()) return;
      busy = true;
      loadRef
        .current()
        .then(({ version }) => {
          if (!live) return;
          if (seen !== null && version !== seen) changeRef.current();
          seen = version;
        })
        .catch(() => {
          /* a missed check is fine; the next one (or the slower full refresh) catches up */
        })
        .finally(() => {
          busy = false;
        });
    };
    check();
    const timer = window.setInterval(check, everyMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [everyMs]);
}
